// utils/mobileOptimizer.js

/**
 * Mobile performance optimization utilities
 */
export class MobileOptimizer {
  static isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  static isLowEndDevice = null;
  static connectionType = null;
  static deviceMemory = null;

  /**
   * Initialize mobile optimizations
   */
  static init() {
    this.detectDeviceCapabilities();
    this.setupConnectionMonitoring();
    this.setupMemoryOptimizations();
    this.setupTouchOptimizations();
    
    console.log('Mobile optimizer initialized:', {
      isMobile: this.isMobile,
      isLowEndDevice: this.isLowEndDevice,
      connectionType: this.connectionType,
      deviceMemory: this.deviceMemory
    });
  }

  /**
   * Detect device capabilities
   */
  static detectDeviceCapabilities() {
    // Detect device memory
    if ('deviceMemory' in navigator) {
      this.deviceMemory = navigator.deviceMemory;
      this.isLowEndDevice = this.deviceMemory <= 2; // 2GB or less
    } else {
      // Fallback detection based on other factors
      this.isLowEndDevice = this.detectLowEndDevice();
    }

    // Detect hardware concurrency
    const cores = navigator.hardwareConcurrency || 1;
    if (cores <= 2) {
      this.isLowEndDevice = true;
    }
  }

  /**
   * Fallback low-end device detection
   */
  static detectLowEndDevice() {
    // Check for performance timing
    if ('performance' in window && 'timing' in performance) {
      const timing = performance.timing;
      const loadTime = timing.loadEventEnd - timing.navigationStart;
      
      // If page load took more than 5 seconds, likely low-end
      if (loadTime > 5000) return true;
    }

    // Check user agent for known low-end devices
    const lowEndPatterns = [
      /Android.*4\.[0-3]/i, // Old Android versions
      /iPhone.*OS [5-8]/i,  // Old iOS versions
      /Windows Phone/i
    ];

    return lowEndPatterns.some(pattern => pattern.test(navigator.userAgent));
  }

  /**
   * Setup connection monitoring
   */
  static setupConnectionMonitoring() {
    if ('connection' in navigator) {
      const connection = navigator.connection;
      this.connectionType = connection.effectiveType;

      connection.addEventListener('change', () => {
        this.connectionType = connection.effectiveType;
        this.adjustForConnection();
      });

      this.adjustForConnection();
    }
  }

  /**
   * Adjust optimizations based on connection
   */
  static adjustForConnection() {
    const connection = navigator.connection;
    if (!connection) return;

    // Adjust image quality based on connection
    if (connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g') {
      this.enableLowBandwidthMode();
    } else if (connection.effectiveType === '3g') {
      this.enableMediumBandwidthMode();
    } else {
      this.enableHighBandwidthMode();
    }

    // Enable data saver mode if requested
    if (connection.saveData) {
      this.enableDataSaverMode();
    }
  }

  /**
   * Enable low bandwidth optimizations
   */
  static enableLowBandwidthMode() {
    console.log('Enabling low bandwidth mode');
    
    // Reduce image quality
    document.documentElement.style.setProperty('--image-quality', '0.6');
    
    // Disable non-essential animations
    document.documentElement.style.setProperty('--animation-duration', '0s');
    
    // Reduce concurrent requests
    this.maxConcurrentRequests = 2;
  }

  /**
   * Enable medium bandwidth optimizations
   */
  static enableMediumBandwidthMode() {
    console.log('Enabling medium bandwidth mode');
    
    document.documentElement.style.setProperty('--image-quality', '0.8');
    document.documentElement.style.setProperty('--animation-duration', '0.2s');
    this.maxConcurrentRequests = 4;
  }

  /**
   * Enable high bandwidth mode
   */
  static enableHighBandwidthMode() {
    console.log('Enabling high bandwidth mode');
    
    document.documentElement.style.setProperty('--image-quality', '1.0');
    document.documentElement.style.setProperty('--animation-duration', '0.3s');
    this.maxConcurrentRequests = 6;
  }

  /**
   * Enable data saver mode
   */
  static enableDataSaverMode() {
    console.log('Enabling data saver mode');
    
    // Disable background refresh
    localStorage.setItem('disableBackgroundRefresh', 'true');
    
    // Reduce cache duration
    localStorage.setItem('reducedCacheDuration', 'true');
    
    // Disable preloading
    localStorage.setItem('disablePreloading', 'true');
  }

  /**
   * Setup memory optimizations
   */
  static setupMemoryOptimizations() {
    if (this.isLowEndDevice) {
      console.log('Applying low-end device optimizations');
      
      // Reduce cache size
      this.maxCacheSize = 50; // Reduce from default 100
      
      // Enable aggressive garbage collection
      this.enableAggressiveGC();
      
      // Reduce concurrent operations
      this.maxConcurrentOperations = 2;
    }

    // Monitor memory usage
    if ('memory' in performance) {
      setInterval(() => {
        const memory = performance.memory;
        const usage = (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100;
        
        if (usage > 80) {
          this.triggerMemoryCleanup();
        }
      }, 10000);
    }
  }

  /**
   * Enable aggressive garbage collection
   */
  static enableAggressiveGC() {
    // Force garbage collection more frequently on low-end devices
    if ('gc' in window && typeof window.gc === 'function') {
      setInterval(() => {
        try {
          window.gc();
        } catch (error) {
          // GC not available
        }
      }, 30000);
    }
  }

  /**
   * Trigger memory cleanup
   */
  static triggerMemoryCleanup() {
    console.log('Triggering memory cleanup');
    
    // Clear old cache entries
    this.clearOldCacheEntries();
    
    // Reduce image cache
    this.reduceImageCache();
    
    // Force garbage collection if available
    if ('gc' in window && typeof window.gc === 'function') {
      try {
        window.gc();
      } catch (error) {
        // GC not available
      }
    }
  }

  /**
   * Clear old cache entries
   */
  static clearOldCacheEntries() {
    const keys = Object.keys(localStorage);
    const now = Date.now();
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    keys.forEach(key => {
      if (key.endsWith('Timestamp')) {
        const timestamp = parseInt(localStorage.getItem(key) || '0');
        if (now - timestamp > maxAge) {
          localStorage.removeItem(key);
          const dataKey = key.replace('Timestamp', '');
          localStorage.removeItem(dataKey);
        }
      }
    });
  }

  /**
   * Reduce image cache
   */
  static reduceImageCache() {
    // This would integrate with image caching system
    console.log('Reducing image cache for memory optimization');
  }

  /**
   * Setup touch optimizations
   */
  static setupTouchOptimizations() {
    if (!this.isMobile) return;

    // Add touch-friendly styles
    const style = document.createElement('style');
    style.textContent = `
      /* Touch-friendly button sizes */
      button, .clickable {
        min-height: 44px;
        min-width: 44px;
      }
      
      /* Reduce touch delay */
      * {
        touch-action: manipulation;
      }
      
      /* Optimize scrolling */
      .scroll-container {
        -webkit-overflow-scrolling: touch;
        overflow-scrolling: touch;
      }
      
      /* Prevent zoom on input focus */
      input, select, textarea {
        font-size: 16px;
      }
    `;
    document.head.appendChild(style);

    // Disable zoom on double tap
    let lastTouchEnd = 0;
    document.addEventListener('touchend', (event) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    }, false);
  }

  /**
   * Get optimized settings for current device
   */
  static getOptimizedSettings() {
    return {
      // Image settings
      imageQuality: this.isLowEndDevice ? 0.7 : 1.0,
      lazyLoadThreshold: this.isLowEndDevice ? '100px' : '50px',
      
      // Animation settings
      enableAnimations: !this.isLowEndDevice,
      animationDuration: this.isLowEndDevice ? 0.1 : 0.3,
      
      // Cache settings
      maxCacheSize: this.isLowEndDevice ? 50 : 100,
      cacheTimeout: this.isLowEndDevice ? 2 * 60 * 1000 : 5 * 60 * 1000,
      
      // Network settings
      maxConcurrentRequests: this.maxConcurrentRequests || (this.isLowEndDevice ? 2 : 4),
      requestTimeout: this.connectionType === '2g' ? 15000 : 10000,
      
      // Rendering settings
      virtualScrolling: this.isLowEndDevice,
      batchSize: this.isLowEndDevice ? 10 : 20,
      
      // Memory settings
      enableMemoryMonitoring: this.isLowEndDevice,
      memoryCleanupThreshold: this.isLowEndDevice ? 70 : 85
    };
  }

  /**
   * Apply mobile-specific optimizations to component
   */
  static optimizeComponent(componentRef) {
    if (!this.isMobile || !componentRef.current) return;

    const element = componentRef.current;
    
    // Add touch-friendly classes
    element.classList.add('mobile-optimized');
    
    // Optimize scroll performance
    if (element.scrollHeight > element.clientHeight) {
      element.style.webkitOverflowScrolling = 'touch';
      element.style.overflowScrolling = 'touch';
    }
    
    // Add passive event listeners for better performance
    const passiveEvents = ['touchstart', 'touchmove', 'wheel'];
    passiveEvents.forEach(event => {
      element.addEventListener(event, () => {}, { passive: true });
    });
  }

  /**
   * Check if feature should be enabled based on device capabilities
   */
  static shouldEnableFeature(feature) {
    const settings = this.getOptimizedSettings();
    
    switch (feature) {
      case 'animations':
        return settings.enableAnimations;
      case 'backgroundRefresh':
        return !localStorage.getItem('disableBackgroundRefresh');
      case 'preloading':
        return !localStorage.getItem('disablePreloading');
      case 'virtualScrolling':
        return settings.virtualScrolling;
      default:
        return true;
    }
  }
}

// Initialize on module load
if (typeof window !== 'undefined') {
  MobileOptimizer.init();
}

export default MobileOptimizer;